import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';

interface User {
  id: string;
  email: string;
  name?: string;
  plan: string;
  scanCredits: number;
  notificationEmail?: string;
  emailNotifications: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (userData: any) => Promise<void>;
  signOut: () => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Check for existing session on mount and when needed
  const checkAuth = async () => {
    try {
      const response = await fetch("/api/user", {
        credentials: "include",
      });
      if (response.ok) {
        setIsAuthenticated(true);
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Expose checkAuth function via context for manual refresh
  const refreshAuth = () => {
    setAuthLoading(true);
    return checkAuth();
  };

  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ["/api/user"],
    enabled: isAuthenticated,
    retry: false,
  });

  // Handle auth errors
  useEffect(() => {
    if (error && isAuthenticated) {
      setIsAuthenticated(false);
    }
  }, [error, isAuthenticated]);

  const signInMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const response = await fetch("/api/auth/signin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for session cookies
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Sign in failed");
      }

      return response.json();
    },
    onSuccess: (data) => {
      // If MFA is required, don't set authenticated yet
      if (data.requiresMFA) {
        // MFA verification needed - handled by signin page
        return;
      }
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
  });

  const signUpMutation = useMutation({
    mutationFn: async (userData: any) => {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for session cookies
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Registration failed");
      }

      return response.json();
    },
    onSuccess: () => {
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
  });

  const signIn = async (email: string, password: string) => {
    await signInMutation.mutateAsync({ email, password });
  };

  const signUp = async (userData: any) => {
    await signUpMutation.mutateAsync(userData);
  };

  const signOut = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsAuthenticated(false);
    queryClient.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        user: user as User || null,
        isLoading: authLoading || isLoading || signInMutation.isPending || signUpMutation.isPending,
        isAuthenticated,
        signIn,
        signUp,
        signOut,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}